/**
 * useTenants — читает tenants + считает связанные счётчики (столы, staff,
 * last_activity) из БД.
 *
 * Доступ: super_admin JWT обходит per-tenant RLS через
 * `tenants_super_admin_all` policy (миграция 00070).
 */
import { useCallback, useEffect, useState } from "react";
import { getSupabaseClient } from "@cafe-os/db";

export interface TenantRow {
  id: string;
  name: { kz?: string; ru?: string; en?: string } | null;
  phone: string | null;
  created_at: string;
  // aggregates
  tables_count: number;
  staff_count: number;
  last_order_at: string | null;
}

export function useTenants(): {
  tenants: TenantRow[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sb = getSupabaseClient();
      // Tenants + aggregates fetched in parallel. Aggregates go through
      // super_admin_tenant_stats RPC (00074) which bypasses staff_select +
      // orders RLS via SECURITY DEFINER — client-side counts returned 0/null
      // because super_admin sessions have no staff JWT claims.
      const [tenantsResp, statsResp] = await Promise.all([
        sb
          .from("tenants")
          .select("id, name, phone, created_at")
          .order("created_at", { ascending: true }),
        sb.rpc("super_admin_tenant_stats"),
      ]);
      if (tenantsResp.error) throw tenantsResp.error;
      if (statsResp.error) throw statsResp.error;

      type StatsRow = {
        tenant_id: string;
        tables_count: number;
        staff_count: number;
        last_order_at: string | null;
      };
      const statsByTenant = new Map<string, StatsRow>();
      for (const row of (statsResp.data ?? []) as StatsRow[]) {
        statsByTenant.set(row.tenant_id, row);
      }

      const list: TenantRow[] = ((tenantsResp.data ?? []) as Array<
        Pick<TenantRow, "id" | "name" | "phone" | "created_at">
      >).map((t) => {
        const stats = statsByTenant.get(t.id);
        return {
          ...t,
          tables_count: stats?.tables_count ?? 0,
          staff_count: stats?.staff_count ?? 0,
          last_order_at: stats?.last_order_at ?? null,
        };
      });
      setTenants(list);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { tenants, loading, error, reload: load };
}

export interface CreateTenantInput {
  slug: string;
  nameRu: string;
  nameKz: string;
  nameEn: string;
  ownerEmail: string;
  ownerName: string;
  /** Город кафе — пробрасывается в дефолтный seed лендинга (адрес-заглушка). */
  city?: string;
  /** Привязать к существующей сети владельца (organizations.id, 00163). */
  orgId?: string;
  /** Создать новую сеть и привязать кафе (id сети = `org-<slug>`). */
  newOrgName?: string;
  /** Существующие тенанты владельца для включения в ту же сеть. */
  attachExistingTenantIds?: string[];
  /** Тариф из шага 3 (SA-1: без него тенант рождается без подписки). */
  planId?: string;
  /** Trial в днях (шаг 3). */
  trialDays?: number;
}

export interface CreateTenantResult {
  tenantSlug: string;
  ownerSetupUrl: string;
  // "auto-added" = Vercel API сам зарегил домен (wildcard DNS + auto SSL).
  // "manual-add-required" = Vercel API fail'нул, нужно вручную в Vercel UI.
  vercelDomain: "auto-added" | "manual-add-required";
  vercelError: string | null;
  /** id сети, к которой привязано кафе (null = одиночное / привязка упала). */
  orgId?: string | null;
  /** Non-fatal ошибка привязки к сети (тенант при этом создан). */
  orgError?: string | null;
  /** id назначенного тарифа (null = подписка не создана). */
  planId?: string | null;
  /** Non-fatal ошибка создания подписки. */
  planError?: string | null;
}

/** Кафе владельца по email (super_admin_lookup_owner_cafes, 00164). */
export interface OwnerCafeRow {
  tenantId: string;
  tenantName: { ru?: string; kz?: string; en?: string };
  orgId: string | null;
  orgName: { ru?: string; kz?: string; en?: string } | null;
}

export async function lookupOwnerCafes(email: string): Promise<OwnerCafeRow[]> {
  const sb = getSupabaseClient();
  const { data, error } = await sb.rpc(
    "super_admin_lookup_owner_cafes" as never,
    { p_email: email } as never,
  );
  if (error) throw error;
  const rows = (data ?? []) as Array<{
    tenant_id: string;
    tenant_name: OwnerCafeRow["tenantName"];
    org_id: string | null;
    org_name: OwnerCafeRow["orgName"];
  }>;
  return rows.map((r) => ({
    tenantId: r.tenant_id,
    tenantName: r.tenant_name ?? {},
    orgId: r.org_id,
    orgName: r.org_name,
  }));
}

export async function createTenant(input: CreateTenantInput): Promise<CreateTenantResult> {
  const sb = getSupabaseClient();
  const { data: sessData } = await sb.auth.getSession();
  const accessToken = sessData.session?.access_token;
  if (!accessToken) throw new Error("Not authenticated");

  const supabaseUrl = (import.meta as unknown as { env: Record<string, string> }).env
    .VITE_SUPABASE_URL;
  const anonKey = (import.meta as unknown as { env: Record<string, string> }).env
    .VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/superAdminCreateTenant`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anonKey,
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      slug: input.slug,
      name: { ru: input.nameRu, kz: input.nameKz, en: input.nameEn },
      ownerEmail: input.ownerEmail,
      ownerName: input.ownerName,
      city: input.city,
      orgId: input.orgId,
      newOrgName: input.newOrgName,
      attachExistingTenantIds: input.attachExistingTenantIds,
      planId: input.planId,
      trialDays: input.trialDays,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message =
      (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${res.status}`;
    throw new Error(message);
  }
  return (await res.json()) as CreateTenantResult;
}
